import { zodResolver } from '@hookform/resolvers/zod'
import { isPast } from 'date-fns'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import WarningCircle from '@/assets/icons/warning-circle.svg?react'
import Alert from '@/components/Alert/Alert'
import Button from '@/components/Button/Button'
import ConfirmModal from '@/components/ConfirmModal'
import FormField from '@/components/FormField'
import ModalForm from '@/components/Modal/ModalForm'
import Textarea from '@/components/Textarea/Textarea'
import DirtyModal from '@/components/common/DirtyModal'
import {
  type CoverTeacherFormSchema,
  formSchemaCoverTeacher
} from '@/constants/cover-payload'
import useTeacherCoverAvailability from '@/hooks/useTeacherCoverAvailability'
import { toast } from '@/hooks/useToast'
import useSetCover from '@/mutations/useSetCover'
import useTeachersAvailability from '@/queries/useTeachersAvailability'
import type { Lesson } from '@/types/lesson'
import {
  getLessonFormattedTime,
  getLessonShortedDetails
} from '@/utils/lesson-details-format'

import CoverSettingAlertContent from './CoverSettingAlertContent'
import styles from './CoverSettingModal.module.scss'
import CoverTeacherField from './CoverTeacherField'

type CoverSettingModalProps = {
  courseName: string
  groupName: string
  lesson: Lesson
  open: boolean
  onOpenChange: (open: boolean) => void
  onCoverChange: () => void
}

const CoverSettingModal = (props: CoverSettingModalProps) => {
  const { t } = useTranslation('common')

  const isPastLesson = !!props.lesson.endDate
    ? isPast(props.lesson.endDate)
    : false

  const [isDirtyModalOpen, setIsDirtyModalOpen] = useState(false)
  const [isCoverLessonModalOpen, setIsCoverLessonModalOpen] = useState(false)

  const { mutate: setCover, isPending: isCoverLoading } = useSetCover(
    props.lesson.id,
    {
      onSuccess: () => {
        toast({
          variant: 'success',
          title: t('toast.cover-created-successfully', {
            LESSON: getLessonFormattedTime(props.lesson)
          })
        })
        props.onCoverChange()
        handleClose()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: t('toast.cover-created-failed', {
            LESSON: getLessonFormattedTime(props.lesson)
          })
        })
        handleClose()
      }
    }
  )

  const { teacher, coTeacher } = props.lesson

  const handleOnCancel = () => {
    isDirty ? setIsDirtyModalOpen(true) : handleClose()
  }

  const form = useForm<CoverTeacherFormSchema>({
    resolver: zodResolver(formSchemaCoverTeacher()),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      teacher: null,
      coTeacher: null,
      comment: ''
    }
  })

  useEffect(() => {
    if (props.open) {
      if (props.lesson.teacherCover) {
        form.setValue('teacher', {
          label: props.lesson.teacherCover.name,
          value: props.lesson.teacherCover.id
        })
      }
      if (props.lesson.coTeacherCover) {
        form.setValue('coTeacher', {
          label: props.lesson.coTeacherCover.name,
          value: props.lesson.coTeacherCover.id
        })
      }
      form.setValue('comment', props.lesson.comment)
    }
  }, [form, props.lesson, props.open])

  const { isDirty } = form.formState

  const { data: teachers, isPending: isTeacherOptionsLoading } =
    useTeachersAvailability({
      startDate: props.lesson.startDate,
      endDate: props.lesson.endDate,
      enabled: props.open,
      ignoreLessonId: [props.lesson.id]
    })

  const handleSubmit = () => {
    if (isAnyWarningExist) setIsCoverLessonModalOpen(true)
    else setCover(form.getValues())
  }

  const handleClose = () => {
    props.onOpenChange(false)
    form.reset()
    setIsCoverLessonModalOpen(false)
  }

  const coverTeacher = form.watch('teacher') || undefined
  const coverCoTeacher = form.watch('coTeacher') || undefined

  const {
    isAnyWarningExist,
    isBothTeacherUnavailable,
    teacherAvailability,
    coTeacherAvailability
  } = useTeacherCoverAvailability({
    lesson: props.lesson,
    teacherList: teachers?.list || [],
    coverTeacher: coverTeacher?.value,
    coverCoTeacher: coverCoTeacher?.value,
    isPastLesson
  })

  return (
    <ModalForm
      title={t('header.cover-lesson', { COURSE: props.courseName })}
      description={getLessonShortedDetails(props.lesson)}
      submitText={t('button.cover')}
      form={form}
      size="md"
      loading={isCoverLoading}
      open={props.open}
      onOpenChange={props.onOpenChange}
      onSubmit={handleSubmit}
      onCancel={handleOnCancel}
      onClose={handleOnCancel}
    >
      <h3 className={styles.label}>{t('header.cover-for')}</h3>
      <div className={styles.fieldContainer}>
        <span className={styles.teacherlabel}>{t('header.main-teacher')}</span>
        <FormField
          label={t('label.select-teacher')}
          control={form.control}
          id="teacher"
          name="teacher"
          render={({ inputProps }) => (
            <CoverTeacherField
              placeholder={t('help.select-teacher')}
              teacher={teacher}
              onClear={() => form.resetField('teacher')}
              inputProps={{
                ...inputProps,
                value: inputProps.value || undefined,
                onChange: value => {
                  inputProps.onChange?.(value)
                  form.trigger()
                }
              }}
              teacherList={teachers?.list || []}
              options={teachers?.options || []}
              isOptionsLoading={isTeacherOptionsLoading}
            />
          )}
        />
      </div>
      {coTeacher ? (
        <div className={styles.fieldContainer}>
          <span className={styles.teacherlabel}>{t('header.co-teacher')}</span>
          <FormField
            label={t('label.select-co-teacher')}
            control={form.control}
            id="coTeacher"
            name="coTeacher"
            render={({ inputProps }) => (
              <CoverTeacherField
                placeholder={t('help.select-co-teacher')}
                teacher={coTeacher}
                onClear={() => form.resetField('coTeacher')}
                inputProps={{
                  ...inputProps,
                  value: inputProps.value || undefined,
                  onChange: value => {
                    inputProps.onChange?.(value)
                    form.trigger()
                  }
                }}
                teacherList={teachers?.list || []}
                options={teachers?.options || []}
                isOptionsLoading={isTeacherOptionsLoading}
              />
            )}
          />
        </div>
      ) : null}

      {form.formState.errors.teacher && form.formState.errors.coTeacher ? (
        <div className={styles.errorContainer}>
          <WarningCircle className={styles.errorIcon} />
          {t('error.one-field-is-required')}
        </div>
      ) : null}

      <FormField
        label={t('label.comment')}
        control={form.control}
        id="comment"
        name="comment"
        render={({ inputProps }) => (
          <Textarea
            placeholder={t('help.type-comment')}
            numberOfLines={3}
            {...inputProps}
          />
        )}
      />

      <div className={styles.alertsWrapper}>
        {isAnyWarningExist ? (
          <Alert
            variant="warning"
            message={
              <CoverSettingAlertContent
                isPastLesson={isPastLesson}
                isCoveredLesson={props.lesson.isCovered}
                isCancelledLesson={props.lesson.isCancelled}
                isBothTeacherUnavailable={isBothTeacherUnavailable}
                teacherAvailability={teacherAvailability}
                coTeacherAvailability={coTeacherAvailability}
              />
            }
          />
        ) : null}
        <Alert variant="info" message={t('help.cover-only-current-lesson')} />
      </div>

      <ConfirmModal
        dataTestId="cover-lesson-confirm-modal"
        header={t('header.cover-lesson-confirm')}
        description={props.lesson ? getLessonShortedDetails(props.lesson) : ''}
        open={isCoverLessonModalOpen}
        onOpenChange={setIsCoverLessonModalOpen}
        alertText={
          <CoverSettingAlertContent
            isPastLesson={isPastLesson}
            isCoveredLesson={props.lesson.isCovered}
            isCancelledLesson={props.lesson.isCancelled}
            isBothTeacherUnavailable={isBothTeacherUnavailable}
            teacherAvailability={teacherAvailability}
            coTeacherAvailability={coTeacherAvailability}
          />
        }
        subheader={
          <>
            <p>{t('help.set-cover-for-this-lesson')}</p>
            <p>{t('help.this-action-will-cause-conflict')}</p>
          </>
        }
        confirmButton={
          <Button
            loading={isCoverLoading}
            variant="danger"
            onClick={() => setCover(form.getValues())}
          >
            {t('button.confirm')}
          </Button>
        }
      />

      <DirtyModal
        isOpen={isDirtyModalOpen}
        onOpenChange={setIsDirtyModalOpen}
        onConfirm={() => {
          handleClose()
          setIsDirtyModalOpen(false)
        }}
      />
    </ModalForm>
  )
}

export default CoverSettingModal
